import { useEffect, useState } from "react";
import StatCard from "pages/dashboard-overview/components/StatCard";
import { fetchExpenseSummary } from "services/expenseService";
import CategoryBreakdownCards from "./CategoryBreakdownCards";
import { subscribeCommissionUpdated } from "utils/commissionEvents";

const formatCurrency = (value) =>
  `$${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const StatCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
    <div className="h-4 w-28 bg-gray-200 animate-pulse rounded" />
    <div className="h-8 w-24 bg-gray-200 animate-pulse rounded mt-2" />
  </div>
);

export default function ExpenseSummaryCards({ api, filters, search, refreshKey = 0 }) {
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadSummary = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchExpenseSummary(api, { search, filters });
        if (!cancelled) setSummary(data);
      } catch {
        if (!cancelled) {
          setSummary(null);
          setError("Failed to load expense summary. Please try again.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadSummary();
    return () => {
      cancelled = true;
    };
  }, [api, filters, search, refreshKey]);

  useEffect(() => {
    return subscribeCommissionUpdated(() => {
      fetchExpenseSummary(api, { search, filters })
        .then((data) => setSummary(data))
        .catch(() => {});
    });
  }, [api, filters, search]);

  if (isLoading) {
    return (
      <div className="p-4 border-b border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[...Array(2)].map((_, i) => (
            <StatCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 border-b border-gray-200">
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-gray-200">
      <div className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <StatCard
            title="Total Expenses"
            value={formatCurrency(summary?.totalExpenses ?? 0)}
            icon="CreditCard"
          />
          <StatCard
            title="Commission Accruals"
            value={formatCurrency(summary?.commissionAccrualTotal ?? 0)}
            icon="Percent"
          />
        </div>
      </div>
      <CategoryBreakdownCards breakdown={summary?.categoryBreakdown} />
    </div>
  );
}
